/**
 * @fileoverview Contact Support page.
 * @description Email/phone contact channels plus a FAQ accordion (native `<details>`, no JS
 * needed). Reachable from AuthLayout's footer, ErrorStateCard's "Contact Support" link, and the
 * other legal pages' cross-links and inline mentions.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import LinkButton from '@/shared/components/atoms/LinkButton'
import LegalLayout from '@/shared/components/layouts/LegalLayout'
import { ROUTES } from '@/shared/constants/routes.constants'

interface ChannelCardProps {
  icon: string
  title: string
  description: string
  value: string
  href: string
  meta: string
}

/**
 * @description Renders one contact-channel card (email or phone).
 * @param {ChannelCardProps} props - The channel's icon/copy/link.
 * @param {string} props.icon - The badge emoji.
 * @param {string} props.title - The channel name (e.g. "Email Support").
 * @param {string} props.description - A short line on when to use this channel.
 * @param {string} props.value - The displayed address/number.
 * @param {string} props.href - The `mailto:`/`tel:` link target.
 * @param {string} props.meta - The response-time/hours note shown below the link.
 */
function ChannelCard({ icon, title, description, value, href, meta }: ChannelCardProps) {
  return (
    <div className="rounded-[14px] border border-divider bg-white p-5.5 shadow-card sm:p-6">
      <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-[10px] bg-primary/10 text-lg">
        {icon}
      </div>
      <h3 className="mb-1 text-sm font-bold text-ink">{title}</h3>
      <p className="mb-2.5 text-[13px] leading-[1.55] text-slate">{description}</p>
      <a href={href} className="inline-block text-sm font-bold text-primary underline">
        {value}
      </a>
      <div className="mt-1.5 text-xs text-footer">{meta}</div>
    </div>
  )
}

interface FaqItemProps {
  question: string
  answer: React.ReactNode
  defaultOpen?: boolean
}

/**
 * @description Renders one FAQ accordion entry.
 * @param {FaqItemProps} props - The question/answer pair and whether it starts expanded.
 * @param {string} props.question - The FAQ question, shown in the `<summary>`.
 * @param {React.ReactNode} props.answer - The FAQ answer, shown when expanded.
 * @param {boolean} [props.defaultOpen] - Whether this entry starts expanded. Off by default.
 */
function FaqItem({ question, answer, defaultOpen = false }: FaqItemProps) {
  return (
    <details
      open={defaultOpen}
      className="group mb-2.5 overflow-hidden rounded-xl border border-divider bg-white"
    >
      <summary className="flex list-none cursor-pointer items-center justify-between gap-3 px-5 py-4 text-[14.5px] font-semibold text-ink [&::-webkit-details-marker]:hidden">
        {question}
        <span className="flex h-5.5 w-5.5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[15px] font-bold leading-none text-primary">
          <span className="group-open:hidden">+</span>
          <span className="hidden group-open:inline">−</span>
        </span>
      </summary>
      <div className="px-5 pb-4.5 text-[13.5px] leading-[1.7] text-slate">{answer}</div>
    </details>
  )
}

/**
 * @component
 */
function ContactSupportPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  return (
    <LegalLayout>
      <div className="mb-7 text-center">
        <h1 className="mb-2 text-[28px] font-extrabold leading-tight text-ink">
          {t('contactSupport.title')}
        </h1>
        <p className="mx-auto max-w-[460px] text-[14.5px] leading-[1.6] text-slate">
          {t('contactSupport.intro')}
        </p>
      </div>

      <div className="mb-10 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <ChannelCard
          icon="✉️"
          title={t('contactSupport.channels.email.title')}
          description={t('contactSupport.channels.email.description')}
          value={t('contactSupport.channels.email.value')}
          href={`mailto:${t('contactSupport.channels.email.value')}`}
          meta={t('contactSupport.channels.email.meta')}
        />
        <ChannelCard
          icon="📞"
          title={t('contactSupport.channels.phone.title')}
          description={t('contactSupport.channels.phone.description')}
          value={t('contactSupport.channels.phone.value')}
          href={`tel:${t('contactSupport.channels.phone.value').replace(/\s+/g, '')}`}
          meta={t('contactSupport.channels.phone.meta')}
        />
      </div>

      <h2 className="mb-4.5 text-[19px] font-extrabold text-ink">
        {t('contactSupport.faq.title')}
      </h2>

      <FaqItem
        defaultOpen
        question={t('contactSupport.faq.resetPassword.question')}
        answer={t('contactSupport.faq.resetPassword.answer')}
      />
      <FaqItem
        question={t('contactSupport.faq.branchAccess.question')}
        answer={t('contactSupport.faq.branchAccess.answer')}
      />
      <FaqItem
        question={t('contactSupport.faq.dataFreshness.question')}
        answer={t('contactSupport.faq.dataFreshness.answer')}
      />
      <FaqItem
        question={t('contactSupport.faq.newTeamMember.question')}
        answer={t('contactSupport.faq.newTeamMember.answer')}
      />
      <FaqItem
        question={t('contactSupport.faq.twoFactor.question')}
        answer={t('contactSupport.faq.twoFactor.answer')}
      />
      <FaqItem
        question={t('contactSupport.faq.browsers.question')}
        answer={t('contactSupport.faq.browsers.answer')}
      />
      <FaqItem
        question={t('contactSupport.faq.security.question')}
        answer={
          <>
            {t('contactSupport.faq.security.answerPrefix')}{' '}
            <LinkButton size="sm" onClick={() => void navigate(ROUTES.PRIVACY_POLICY)}>
              {t('legalNav.privacyPolicy')}
            </LinkButton>{' '}
            {t('contactSupport.faq.security.answerSuffix')}
          </>
        }
      />
    </LegalLayout>
  )
}

export default ContactSupportPage
